import { Injectable } from '@angular/core';

import { LMUrlUtilService } from '@services/utils/lm-url-util.service';

/**
 * Created by vwiny on 9/6/2018.
 */
@Injectable()
export class LMBannerSearchService {
  constructor(private lmUrlUtilService: LMUrlUtilService) {}
  static readonly NO_MENU_SEARCH_LIMIT = 5;

  public menuNameMatch = false;

  public getMenuSearchUrl(menuSearchString: string): string {
    let menuSearchUrl = this.lmUrlUtilService.getBannerSpecUrl();

    const delimiter = (menuSearchUrl.indexOf('?') < 0) ? '?' : '&';
    menuSearchUrl += delimiter + 'requestType=searchMenu&menuSearch=' + menuSearchString;

    return menuSearchUrl;
  }

  public matchMultiMenus(menuSpecs: Array<any>, searchString: string, bannerSearchInfo?: LMBannerSearchInfo): {[ key: string ]: Array<LMBannerSearchResult>} {
    if (!bannerSearchInfo) {
      bannerSearchInfo = this.getBannerSearchInfo(searchString);
    }

    const menuResult: { [ key: string ]: Array<any> } = {};

    menuSpecs.forEach(menuSpec => {
      let searchResult: Array<LMBannerSearchResult> = [];

      if (menuSpec.webAppName !== 'web') {
        if (!bannerSearchInfo.limitSearch  || searchResult.length < LMBannerSearchService.NO_MENU_SEARCH_LIMIT) {
          searchResult = this.matchMenus(menuSpec.menuSpec, bannerSearchInfo.searchString, bannerSearchInfo);
          if (searchResult.length > 0) {
            menuResult [ menuSpec.webAppName ] = searchResult;
          }
        }
      }
    });

    return menuResult;
  }

  private matchMenus(menuSpecs: Array<any>, searchString: string, bannerSearchInfo: LMBannerSearchInfo): Array<LMBannerSearchResult> {
    const searchResult: Array<LMBannerSearchResult> = [];

    for (let i = 0; i < menuSpecs.length; i++) {
      if (bannerSearchInfo.limitSearch && searchResult.length >= LMBannerSearchService.NO_MENU_SEARCH_LIMIT) {
        break;
      }

      this.findMenuMatch(searchResult, menuSpecs[ i ], searchString, bannerSearchInfo);

      this.menuNameMatch = false;
    }
    searchResult.sort((menu1: LMBannerSearchResult, menu2: LMBannerSearchResult) => menu1.child.localeCompare(menu2.child));

    return searchResult;
  }

  public matchData(dataMenus: Array<LMBannerDataMenu>): Array<LMBannerSearchResult> {
    const searchResult: Array<LMBannerSearchResult> = [];

    if (dataMenus) {
      for (let i = 0; i < dataMenus.length; i++) {
        this.findDataMatch(searchResult, dataMenus[ i ]);
      }
    }
    return searchResult;
  }

  public matchReports(reports: Array<LMBannerReportMenu>): Array<LMBannerSearchResult> {
    const searchResult: Array<LMBannerSearchResult> = [];

    if (reports) {
      reports.forEach(report => this.findReportMatch(searchResult, report));
    }

    return searchResult;
  }

  // todo vonnyw - element id project - how to type menuSpec? why is it not just LMMenuItemSpec?
  private findMenuMatch(searchResult: Array<LMBannerSearchResult>, menuSpec: any, searchString: string, bannerSearchInfo: LMBannerSearchInfo): void {

    if (menuSpec.menu) {
      for (let i = 0; i < menuSpec.menu.length; i++) {
        if (bannerSearchInfo.limitSearch && searchResult.length >= LMBannerSearchService.NO_MENU_SEARCH_LIMIT) {
          break;
        }

        if (!this.menuNameMatch) {
          this.matchMenuName(searchResult, menuSpec, searchString, bannerSearchInfo);
        }
        this.findMenuMatch(searchResult, menuSpec.menu[ i ], searchString, bannerSearchInfo);
      }

      this.matchSearchTerm(searchResult, menuSpec, searchString, bannerSearchInfo);
    } else {
      const menuSpecLabel: string = menuSpec.label as string;

      if (bannerSearchInfo.searchType === 'STARTSWITH') {
        searchString = '^' + searchString;
      } else if (bannerSearchInfo.searchType === 'ENDSWITH') {
        searchString = searchString + '$';
      }

      const pattern = new RegExp(searchString.replace(/\*/g, '.*')
          .replace('\\', '\\\\'), 'i');

      if (pattern.test(menuSpecLabel)) {
        const result: LMBannerSearchResult = {
          id: menuSpec.id,
          type: 'Applications',
          parent: menuSpec.parentLabel,
          child: menuSpec.label,
          url: menuSpec.link
        }
        searchResult.push(result);
      }

      this.matchSearchTerm(searchResult, menuSpec, searchString, bannerSearchInfo);
    }
  }

  private findReportMatch(searchResult: Array<LMBannerSearchResult>, report: LMBannerReportMenu): void {
    searchResult.push({ id: report.id, type: report.type, child: report.name, url: report.url });
  }

  private findDataMatch(searchResult: Array<LMBannerSearchResult>, dataMenu: LMBannerDataMenu): void {
    if (dataMenu.type === 'CLASS') {
      searchResult.push({
        automationId: dataMenu.automationId,
        id: LMBannerSearchService.toHtmlElementId(dataMenu.id),
        type: 'Data',
        parent: dataMenu.moduleName + ' / ' + 'Business Classes',
        child: dataMenu.name,
        url: dataMenu.url
      });
    } else if (dataMenu.type === 'TASK') {
      searchResult.push({
        automationId: dataMenu.automationId,
        id: LMBannerSearchService.toHtmlElementId(dataMenu.id),
        type: 'Data',
        parent: dataMenu.moduleName + ' / ' + 'Business Tasks' + ' / ' + dataMenu.name,
        child: dataMenu.name + '.' + dataMenu.command,
        url: dataMenu.url,
        resourceId: dataMenu.resourceId
      });
    }
  }

  public getBannerSearchInfo(menuSearchValue: string): LMBannerSearchInfo {
    let menuSearchType: LMBannerSearchType = null;
    let menuSearchString: string = null;

    if (menuSearchValue.startsWith('*')) {
      menuSearchType = 'STARTSWITH';
      menuSearchString = menuSearchValue.substring(1, menuSearchValue.length);
    } else if (menuSearchValue.endsWith('*')) {
      menuSearchType = 'ENDSWITH';
      menuSearchString = menuSearchValue.substring(0, menuSearchValue.length - 1)
    } else {
      menuSearchType = 'CONTAINS';
      menuSearchString = menuSearchValue;
    }

    return {
      searchString: menuSearchString,
      searchType: menuSearchType,
      limitSearch: menuSearchValue.length <= 2 ? true : false
    };
  }

  /**
   * @param accessibleWebApps
   * @param searchResultsByWebApp { [webAppName]: LMBannerSearchResults[] }
   * @param currentWebApp
   */
  public buildWebAppSpecsFromSearchResults(
    accessibleWebApps: LMWebAppSpec[],
    searchResultsByWebApp: { [ key: string ]: LMBannerSearchResult[] },
    currentWebApp: string
  ): LMWebAppSpec[] {
    const webAppsWithSearchResults: LMWebAppSpec[] = [];

    Object.keys(searchResultsByWebApp).forEach(webAppName => accessibleWebApps.find(webAppSpec => {
      if (webAppSpec.webAppName === webAppName) {
        if (webAppName === currentWebApp) {
          webAppsWithSearchResults.splice(0, 0, webAppSpec);
        } else {
          webAppsWithSearchResults.push(webAppSpec);
        }
      }
    }));

    return webAppsWithSearchResults;
  }

  private matchMenuName(searchResult: Array<LMBannerSearchResult>, menuSpec: any, searchString: string, bannerSearchInfo: LMBannerSearchInfo): void {

    const menuSpecLabel: string = menuSpec.menu[0].parentLabel as string;
    if (bannerSearchInfo.searchType === 'STARTSWITH') {
      searchString = '^' + searchString;
    } else if (bannerSearchInfo.searchType === 'ENDSWITH') {
      searchString = searchString + '$';
    }

    const pattern = new RegExp(searchString.replace(/\*/g, '.*')
        .replace('\\', '\\\\'), 'i');

    if (pattern.test(menuSpecLabel) && (menuSpec.menu && menuSpec.menu.length > 0)) {
      const result: LMBannerSearchResult = {
        id:        menuSpec.id,
        type:      'Applications',
        parent:    menuSpec.parentLabel,
        child:     menuSpec.label,
        url:       menuSpec.link,
        menu:      menuSpec.menu
      };
      searchResult.push(result);
      this.menuNameMatch = true;
    }
  }

  private matchSearchTerm(searchResult: Array<LMBannerSearchResult>, menuSpec: any, searchString: string, bannerSearchInfo: LMBannerSearchInfo): void {

    if (menuSpec.menuItemTerms) {
      for (let n = 0; n < menuSpec.menuItemTerms.length; n++) {
        const searchTerm: string = menuSpec.menuItemTerms[n] as string;

        if (bannerSearchInfo.searchType === 'STARTSWITH') {
          searchString = '^' + searchString;
        } else if (bannerSearchInfo.searchType === 'ENDSWITH') {
          searchString = searchString + '$';
        }

        const pattern = new RegExp(searchString.replace(/\*/g, '.*')
            .replace('\\', '\\\\'), 'i');

        if (pattern.test(searchTerm) && (menuSpec.menu && menuSpec.menu.length > 0)) {
          const result: LMBannerSearchResult = {
            id:           menuSpec.id,
            automationId: menuSpec.automationId,
            type:         'Applications',
            parent:       menuSpec.parentLabel,
            child:        menuSpec.label,
            url:          menuSpec.link,
            menu:         menuSpec.menu,
            searchTerm:   searchTerm
          };
          searchResult.push(result);
        } else if (pattern.test(searchTerm)) {
          const result: LMBannerSearchResult = {
            id:           menuSpec.id,
            automationId: menuSpec.automationId,
            type:         'Applications',
            parent:       menuSpec.parentLabel,
            child:        menuSpec.label,
            url:          menuSpec.link,
            searchTerm:   searchTerm
          };
          searchResult.push(result);
        }
      }
    }

    if (menuSpec.menuTerms) {
      for (let n = 0; n < menuSpec.menuTerms.length; n++) {
        const searchTerm: string = menuSpec.menuTerms[n] as string;

        if (bannerSearchInfo.searchType === 'STARTSWITH') {
          searchString = '^' + searchString;
        } else if (bannerSearchInfo.searchType === 'ENDSWITH') {
          searchString = searchString + '$';
        }

        const pattern = new RegExp(searchString.replace(/\*/g, '.*')
            .replace('\\', '\\\\'), 'i');

        if (pattern.test(searchTerm)) {
          const result: LMBannerSearchResult = {
            id:           menuSpec.id,
            automationId: menuSpec.automationId,
            type:         'Applications',
            parent:       menuSpec.parentLabel,
            child:        menuSpec.label,
            url:          menuSpec.link,
            menu:         menuSpec.menu,
//            searchTerm:   searchTerm
          };
          searchResult.push(result);
        }
      }
    }
  }

  private static toHtmlElementId(id: string) {
    return id.replace('.', '_');
  }
}
